import { useEffect } from 'react';

/**
 * Hook to register global keyboard shortcuts. Supports single-key ('?', '/') and
 * two-key sequences like 'g a' (press 'g' then 'a' within 1s).
 *
 * Shortcuts: array of { keys: '?' | '/' | 'g a', handler: () => void, description? }
 *
 * Ignored while the user is typing in an input/textarea/contenteditable.
 */
const SEQUENCE_WINDOW_MS = 1000;

const isTypingTarget = (target) => {
    if (!target) return false;
    const tag = (target.tagName || '').toUpperCase();
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (target.isContentEditable) return true;
    return false;
};

export const useKeyboardShortcuts = (shortcuts) => {
    useEffect(() => {
        if (!shortcuts || !shortcuts.length) return undefined;

        let pendingPrefix = null;
        let pendingTimer = null;

        const clearPending = () => {
            pendingPrefix = null;
            if (pendingTimer) {
                clearTimeout(pendingTimer);
                pendingTimer = null;
            }
        };

        const handle = (event) => {
            if (isTypingTarget(event.target)) return;
            if (event.metaKey || event.ctrlKey || event.altKey) return;

            const key = event.key;

            if (pendingPrefix) {
                const sequence = `${pendingPrefix} ${key}`;
                const match = shortcuts.find((s) => s.keys === sequence);
                clearPending();
                if (match) {
                    event.preventDefault();
                    match.handler(event);
                }
                return;
            }

            const singleMatch = shortcuts.find((s) => s.keys === key);
            if (singleMatch) {
                event.preventDefault();
                singleMatch.handler(event);
                return;
            }

            const prefixCandidate = shortcuts.some((s) => s.keys.startsWith(`${key} `));
            if (prefixCandidate) {
                pendingPrefix = key;
                pendingTimer = setTimeout(clearPending, SEQUENCE_WINDOW_MS);
            }
        };

        window.addEventListener('keydown', handle);
        return () => {
            window.removeEventListener('keydown', handle);
            clearPending();
        };
    }, [shortcuts]);
};
